/**
 * NEXUMA — WorkdayTimeline
 *
 * The one place in Nexuma that shows a working day as it actually happened,
 * against the day that was planned. Modelled on the artifact this system
 * replaces — the punched time card — rather than on a generic progress bar:
 * an hour ruler runs underneath, the planned shift is a quiet recessed track,
 * and the worked time is punched into it.
 *
 *   ┌─ planned shift ──────────────────────────────────────────┐
 *   │███████████████░░░░░░░░████████████▌                      │   ← worked / break / now
 *   └──────────────────────────────────────────────────────────┘
 *    08    09    10    11    12    13    14    15    16    17
 *      ▲check-in            ▲pause            ▲now
 *
 * A bar answers "how far along am I". This answers "when did I start, where did
 * the time go, and when do I leave" — the three questions a shift worker
 * actually asks. That is why it earns the space; it is not decoration.
 *
 * Accessibility: the graphic is `role="img"` with a full sentence summary in
 * `aria-label`, and every segment is also emitted as a visually-hidden list so
 * screen readers get the sequence, not just the summary. Nothing here is
 * conveyed by colour alone — each segment type has its own fill pattern and is
 * named in the legend.
 */
import React, { useMemo } from 'react';
import { Box, Stack, Typography, Tooltip, alpha, useTheme, useMediaQuery } from '@mui/material';

/* ── Time helpers ──────────────────────────────────────────────────────────── */

const pad2 = (n) => String(n).padStart(2, '0');

/** "08:30" → minutes since midnight. Returns null for anything unparseable. */
export function hhmmToMin(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return h * 60 + min;
}

/** Date/ISO → minutes since midnight of its own local day. */
function dateToMin(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
}

const minToHHMM = (min) => `${pad2(Math.floor(min / 60) % 24)}:${pad2(Math.round(min) % 60)}`;

/** "7h 30min" / "45min" — the app's duration voice, matching fmtHM elsewhere. */
function fmtDur(min) {
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${pad2(r)}min` : `${r}min`;
}

/* ── Segment model ─────────────────────────────────────────────────────────── */

/**
 * Turn a session into ordered, non-overlapping segments across the day.
 * Work spans are whatever is left between the breaks.
 *
 * @param {object} session { check_in, check_out, breaks:[{start,end,type}] }
 * @param {number} nowMin  minutes-since-midnight used as the open end
 */
function buildSegments(session, nowMin) {
  const inMin = dateToMin(session?.check_in);
  if (inMin == null) return [];

  const outMin = dateToMin(session?.check_out);
  // An open session runs to "now"; a closed one to its check-out.
  const endMin = outMin != null ? outMin : Math.max(inMin, nowMin);

  const breaks = (session?.breaks || [])
    .map((b) => {
      const s = dateToMin(b?.start);
      if (s == null) return null;
      const e = dateToMin(b?.end);
      return {
        start: Math.max(inMin, s),
        // An open break also runs to "now".
        end: Math.min(endMin, e == null ? endMin : e),
        type: b?.type || 'break',
        open: e == null,
      };
    })
    .filter((b) => b && b.end > b.start)
    .sort((a, b) => a.start - b.start);

  const segments = [];
  let cursor = inMin;
  breaks.forEach((b) => {
    if (b.start > cursor) segments.push({ kind: 'work', start: cursor, end: b.start });
    segments.push({ kind: 'break', start: b.start, end: b.end, type: b.type, open: b.open });
    cursor = Math.max(cursor, b.end);
  });
  if (endMin > cursor) segments.push({ kind: 'work', start: cursor, end: endMin });

  return segments;
}

/* ── Component ─────────────────────────────────────────────────────────────── */

/**
 * @param {object}  props
 * @param {object}  props.session      { check_in, check_out, breaks:[{start,end,type}] }
 * @param {object}  props.shift        { start_time:'08:00', end_time:'16:00' }
 * @param {Date}   [props.now]         Injected clock, so the caller owns the tick.
 * @param {boolean}[props.showLegend]  Render the segment legend (default true).
 * @param {number} [props.height]      Track height in px (default 40).
 * @param {func}   [props.t]           i18n `t`; falls back to Albanian defaults.
 */
export default function WorkdayTimeline({
  session = {}, shift = {}, now = new Date(),
  showLegend = true, height = 40, t, sx,
}) {
  const theme = useTheme();
  // On a 375px phone the track is ~300px wide, so a 12-hour window at one tick
  // per hour leaves ~25px per label and they crowd. Halve the density there.
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const tr = (key, def, opts) => (typeof t === 'function' ? t(key, def, opts) : def);

  const nowMin = now.getHours() * 60 + now.getMinutes();

  const model = useMemo(() => {
    const shiftStart = hhmmToMin(shift?.start_time);
    const shiftEnd = hhmmToMin(shift?.end_time);
    const segments = buildSegments(session, nowMin);

    // The visible window must contain the planned shift AND everything that
    // actually happened — an early check-in or overtime must never be clipped
    // off the edge, or the picture lies. One hour of air on each side.
    const marks = [
      ...(shiftStart != null ? [shiftStart] : []),
      ...(shiftEnd != null ? [shiftEnd] : []),
      ...segments.flatMap((s) => [s.start, s.end]),
    ];
    // Nothing to draw yet: fall back to the planned shift, else a default day.
    const lo = marks.length ? Math.min(...marks) : (shiftStart ?? 8 * 60);
    const hi = marks.length ? Math.max(...marks) : (shiftEnd ?? 17 * 60);

    const from = Math.max(0, Math.floor((lo - 60) / 60) * 60);
    const to = Math.min(24 * 60, Math.ceil((hi + 60) / 60) * 60);
    const span = Math.max(60, to - from);

    const workedMin = segments.filter((s) => s.kind === 'work')
      .reduce((acc, s) => acc + (s.end - s.start), 0);
    const breakMin = segments.filter((s) => s.kind === 'break')
      .reduce((acc, s) => acc + (s.end - s.start), 0);

    // Hour ticks; thinned on long windows so labels never collide. The closing
    // boundary gets its own tick unless a regular one already sits near it —
    // otherwise a 2-hour step can leave the last hour of the window unlabelled.
    const hours = [];
    const base = span > 12 * 60 ? 120 : 60;
    const step = narrow ? base * 2 : base;
    for (let m = from; m <= to; m += step) hours.push(m);
    if (to - hours[hours.length - 1] >= step / 2) hours.push(to);

    return {
      from, to, span, segments, hours, workedMin, breakMin,
      shiftStart, shiftEnd,
      checkIn: dateToMin(session?.check_in),
      checkOut: dateToMin(session?.check_out),
      nowVisible: nowMin >= from && nowMin <= to,
    };
  }, [session, shift, nowMin, narrow]);

  const pct = (min) => ((min - model.from) / model.span) * 100;

  const C = {
    work:  theme.palette.primary.main,
    break: theme.palette.warning.main,
    plan:  theme.palette.grey[200],
    now:   theme.palette.text.primary,
  };

  /* Plain-language summary — this is what a screen reader announces, and it is
   * the same sentence a sighted user could read off the graphic. */
  const summary = useMemo(() => {
    if (model.checkIn == null) {
      return tr('timeline.a11y.notStarted', 'Dita e punës nuk ka filluar ende.');
    }
    const parts = [
      tr('timeline.a11y.checkedIn', 'Check-in në {{time}}.', { time: minToHHMM(model.checkIn) }),
      tr('timeline.a11y.worked', 'Punuar {{dur}}.', { dur: fmtDur(model.workedMin) }),
    ];
    if (model.breakMin > 0) {
      parts.push(tr('timeline.a11y.paused', 'Pauzë {{dur}}.', { dur: fmtDur(model.breakMin) }));
    }
    parts.push(model.checkOut != null
      ? tr('timeline.a11y.checkedOut', 'Check-out në {{time}}.', { time: minToHHMM(model.checkOut) })
      : tr('timeline.a11y.stillOpen', 'Ende në punë.'));
    return parts.join(' ');
  }, [model, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const legend = [
    { key: 'work',  color: C.work,  label: tr('timeline.legend.work', 'Punë') },
    { key: 'break', color: C.break, label: tr('timeline.legend.break', 'Pauzë') },
    { key: 'plan',  color: C.plan,  label: tr('timeline.legend.planned', 'Orari i planifikuar') },
  ];

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {/* ── The graphic ────────────────────────────────────────────────────── */}
      <Box
        role="img"
        aria-label={summary}
        sx={{ position: 'relative', width: '100%', height, mb: 0.5 }}
      >
        {/* Planned shift — a recessed track the real day is punched into. */}
        {model.shiftStart != null && model.shiftEnd != null && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute', top: 0, height: '100%',
              left: `${pct(model.shiftStart)}%`,
              width: `${pct(model.shiftEnd) - pct(model.shiftStart)}%`,
              bgcolor: C.plan,
              borderRadius: '8px',
            }}
          />
        )}

        {/* Full-width hairline so the window reads as a day even before check-in. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute', top: '50%', left: 0, right: 0, height: '1px',
            bgcolor: 'divider', transform: 'translateY(-50%)', zIndex: 0,
          }}
        />

        {/* Actual segments. Breaks are hatched as well as amber, so the two
            never depend on hue alone. */}
        {model.segments.map((s, i) => {
          const isWork = s.kind === 'work';
          const color = isWork ? C.work : C.break;
          const label = isWork
            ? `${tr('timeline.legend.work', 'Punë')} · ${minToHHMM(s.start)}–${minToHHMM(s.end)} · ${fmtDur(s.end - s.start)}`
            : `${tr('timeline.legend.break', 'Pauzë')} · ${minToHHMM(s.start)}–${minToHHMM(s.end)} · ${fmtDur(s.end - s.start)}`;
          return (
            <Tooltip key={`${s.kind}-${s.start}-${i}`} title={label} arrow>
              <Box
                sx={{
                  position: 'absolute', top: 4, height: 'calc(100% - 8px)',
                  left: `${pct(s.start)}%`,
                  width: `${Math.max(0.6, pct(s.end) - pct(s.start))}%`,
                  borderRadius: '6px',
                  zIndex: 1,
                  bgcolor: isWork ? color : alpha(color, 0.22),
                  border: isWork ? 'none' : `1px solid ${alpha(color, 0.55)}`,
                  // Diagonal hatch marks a break without relying on its colour.
                  backgroundImage: isWork ? 'none' : `repeating-linear-gradient(
                    45deg, ${alpha(color, 0.55)} 0 2px, transparent 2px 6px)`,
                  transition: theme.motion?.surface,
                }}
              />
            </Tooltip>
          );
        })}

        {/* "Now" marker — only while the day is genuinely in progress. */}
        {model.nowVisible && model.checkOut == null && model.checkIn != null && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute', top: -2, bottom: -2, zIndex: 2,
              left: `${pct(nowMin)}%`, width: '2px',
              bgcolor: C.now, borderRadius: '2px',
              '&::after': {
                content: '""', position: 'absolute', top: -3, left: -3,
                width: 8, height: 8, borderRadius: '50%', bgcolor: C.now,
              },
            }}
          />
        )}
      </Box>

      {/* ── Hour ruler ─────────────────────────────────────────────────────── */}
      <Box aria-hidden sx={{ position: 'relative', height: 16, mb: showLegend ? 1 : 0 }}>
        {model.hours.map((m) => (
          <Box
            key={m}
            sx={{
              position: 'absolute', left: `${pct(m)}%`, top: 0,
              transform: 'translateX(-50%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}
          >
            <Box sx={{ width: '1px', height: 4, bgcolor: 'divider' }} />
            <Typography
              className="tabular"
              sx={{ fontSize: '0.625rem', color: 'text.secondary', lineHeight: 1.4 }}
            >
              {pad2(Math.floor(m / 60))}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Legend + totals ──────────────────────────────────────────────────
          `space-between` rather than a flex:1 spacer — a spacer inside a
          wrapping row collapses to zero on the wrapped line, which jams the
          totals against the legend on narrow screens. */}
      {showLegend && (
        <Stack
          direction="row" flexWrap="wrap" alignItems="center"
          columnGap={1.5} rowGap={0.5}
          sx={{ mt: 0.5, justifyContent: 'space-between' }}
        >
          <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={1.5} rowGap={0.5}>
          {legend.map((l) => (
            <Stack key={l.key} direction="row" alignItems="center" spacing={0.625}>
              <Box
                aria-hidden
                sx={{
                  width: 10, height: 10, borderRadius: '3px', bgcolor: l.color,
                  border: l.key === 'plan' ? `1px solid ${theme.palette.grey[300]}` : 'none',
                  backgroundImage: l.key === 'break' ? `repeating-linear-gradient(
                    45deg, ${alpha(theme.palette.warning.dark, 0.5)} 0 2px, transparent 2px 5px)` : 'none',
                }}
              />
              <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 500 }}>
                {l.label}
              </Typography>
            </Stack>
          ))}
          </Stack>

          <Typography className="tabular" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
            {tr('timeline.totals.worked', 'Punuar')}{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {fmtDur(model.workedMin)}
            </Box>
            {model.breakMin > 0 && (
              <>
                {' · '}
                {tr('timeline.legend.break', 'Pauzë')}{' '}
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {fmtDur(model.breakMin)}
                </Box>
              </>
            )}
          </Typography>
        </Stack>
      )}

      {/* Screen-reader sequence — the ordered events behind the graphic, which
          the single aria-label summary can't convey. */}
      <Box
        component="ul"
        sx={{
          position: 'absolute', width: '1px', height: '1px', overflow: 'hidden',
          clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'nowrap',
          p: 0, m: 0,
        }}
      >
        {model.segments.map((s, i) => (
          <li key={`sr-${s.kind}-${s.start}-${i}`}>
            {s.kind === 'work'
              ? tr('timeline.legend.work', 'Punë')
              : tr('timeline.legend.break', 'Pauzë')}
            {`: ${minToHHMM(s.start)}–${minToHHMM(s.end)}, ${fmtDur(s.end - s.start)}`}
          </li>
        ))}
      </Box>
    </Box>
  );
}
